// noinspection JSUnusedGlobalSymbols
import queryString from "query-string";
import * as Sentry from "@sentry/gatsby";
import {
  ATTRIBUTION_KEY,
  PLATFORM,
  ISLOCALHOST,
  VERSION,
  REFERRAL_KEY
} from "./src/appConfig";
import localObject from "utilities/localObject";
import { isWebView, isAppleWebView, isAndroidWebView } from "utilities/display";
import { removeTrailingSlash } from "utilities/strings";
import { wrapApp } from "./src/wrapApp";
import "@picklebet/design-tokens/styles/plus-jakarta-sans.css";
import "@picklebet/design-tokens/styles/bebas-neue.css";
import "@fontsource/dm-mono";
import "@picklebet/design-tokens/tokens.css";
import "@picklebet/ui/style.css";
import "core-js/actual/object/has-own";


/**
 * Implement Gatsby's Browser APIs in this file.
 *
 * See: https://www.gatsbyjs.org/docs/browser-apis/
 */
export const onClientEntry = () => {
  //Log here so that the version is forwarded to full story and visible in the dev tool section
  console.log("Client Version: " + VERSION);
  console.log("Client Platform: " + PLATFORM);

  try {
    if (window.localStorage.getItem("userPreferences") === "undefined") {
      console.log("Erasing corrupted user preferences");
      window.localStorage.removeItem("userPreferences");
    }
  } catch {
    console.warn("Failed to erase corrupted user preferences");
  }


  if (isAppleWebView()) {
    Sentry.setTag("mobile-web-view", "Apple");
  }

  if (isAndroidWebView()) {
    Sentry.setTag("mobile-web-view", "Android");
  }

  const isChunkLoadError = (err) => {
    const msg = String(err?.message ?? err ?? "");
    return (
      err?.name === "ChunkLoadError" ||
      /Loading chunk [\w-]+ failed/i.test(msg) ||
      /Failed to fetch dynamically imported module/i.test(msg) ||
      /error loading dynamically imported module/i.test(msg) ||
      /Importing a module script failed/i.test(msg)
    );
  };

  const RELOAD_KEY = "__chunkReloadAt";
  const LOOP_WINDOW_MS = 10_000;

  const reloadOnce = () => {
    const previous = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
    if (Date.now() - previous < LOOP_WINDOW_MS) return;
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
    window.location.reload();
  };

  window.addEventListener("error", (event) => {
    if (isChunkLoadError(event.error ?? event.message)) {
      event.preventDefault();
      reloadOnce();
    }
  });

  window.addEventListener("unhandledrejection", (event) => {
    if (isChunkLoadError(event.reason)) {
      event.preventDefault();
      reloadOnce();
    }
  });
};

let routeJustChangedMarker = null;

export const onRouteUpdate = ({ location, prevLocation }) => {
  /*
    Gatsby fires onRouteUpdate twice on first page load
    this hack makes sure that any route change that happened
    in last 500ms is not being tracked
   */
  if (!routeJustChangedMarker) {
    routeJustChangedMarker = setTimeout(() => {
      routeJustChangedMarker = null;
    }, 500);
  } else {
    return;
  }

  const queryParams = queryString.parse(location.search);
  const referralCode = queryParams.r ?? queryParams.promotionCode ?? null;

  if (typeof window !== "undefined") {
    const attribution = {};
    Object.entries(queryParams).forEach(([k, v]) => {
      //cid and clickId are parameters used by affiliate tracking software
      if (k.startsWith("utm_")) {
        attribution[k] = v;
      }
      if (k === "cid" || k.toLowerCase() === "clickid") {
        //normalise the various case combinations to clickId
        attribution["clickId"] = v;
      }
      if (k.toLowerCase() === "btag") {
        attribution["btag"] = v;
      }
      if (k === "token") {
        attribution["token"] = v;
      }
      if (k === "affiliate_id") {
        attribution["affiliate_id"] = v;
      }
    });

    if (Object.keys(attribution).length > 0) {
      localObject.setValue(ATTRIBUTION_KEY, attribution);
    }

    if (referralCode) {
      localObject.setValue(REFERRAL_KEY, referralCode);
    }

    // we only want to track "real" route changes
    const shouldTrackPageEvent = location?.pathname !== prevLocation?.pathname;

    // Segment.js
    if (
      window.analytics &&
      !ISLOCALHOST &&
      !isWebView() &&
      shouldTrackPageEvent
    ) {
      /* global analytics */
      analytics.page(
        null,
        null,
        {
          referralCode,
          clientVersion: VERSION,
        },
        null,
      );
    }
  }
};

export const shouldUpdateScroll = ({
  routerProps: { location },
  prevRouterProps,
  getSavedScrollPosition,
}) => {
  const currentPosition = getSavedScrollPosition(location);

  // List of pages where scroll will be restored manually
  const noScrollPages = [
    /^\/$/, // main page
    /^\/en-au\/?$/, // main page with the language prefix
    /\/match\/?/, // match page
    /\/betting\/?/, // betting pages
    /\/pickems\/?/, // pickems pages
    /\/casino\/?/, // casino pages
  ];

  const shouldScroll = !noScrollPages.some((regex) =>
    regex.test(location.pathname),
  );

  const previousPathname = prevRouterProps?.location?.pathname;
  // "/a" and "/a/" are the same page (trailingSlash: "always"). Some links land
  // without the slash and the first in-page navigation adds it, which must not
  // count as a page change.
  const isSamePage =
    previousPathname !== undefined &&
    removeTrailingSlash(previousPathname) ===
      removeTrailingSlash(location.pathname);

  // restore scroll to 0 if we are navigating to a new page
  if (!isSamePage) {
    // Ref. https://github.com/gatsbyjs/gatsby/issues/38201
    setTimeout(() => {
      window.scrollTo(...(currentPosition || [0, 0]));
    }, 0);
  }

  if (!shouldScroll) {
    return false;
  }

  return true;
};

export const wrapRootElement = wrapApp;

export const registerServiceWorker = () => true;

export { wrapPageElement } from "./src/gatsby/wrapPageElement";
